import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { LetDirective } from '@ngrx/component';
import { ChartedItemsSurfaceListComponent } from '@principle-theorem/ng-clinical-charting';
import { TreatmentCategoryIndicatorComponent } from '@principle-theorem/ng-principle-shared';
import {
  MapPipe,
  TruncateTooltipDirective,
} from '@principle-theorem/ng-shared';
import {
  ChartedServiceExclusiveGroup,
  ChartedServiceSmartGroup,
  ChartedTreatment,
  ServiceCodeEntry,
} from '@principle-theorem/principle-core';
import {
  IChartedTreatment,
  IPricedServiceCodeEntry,
  ITreatmentCategory,
  ITreatmentConfiguration,
  ServiceCodeGroupType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  WithRef,
  getDoc,
  shareReplayCold,
} from '@principle-theorem/shared';
import { flatten } from 'lodash';
import { Observable, ReplaySubject, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'pr-appointment-history-card-treatment',
  templateUrl: './appointment-history-card-treatment.component.html',
  styleUrls: ['./appointment-history-card-treatment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltip,
    NgTemplateOutlet,
    LetDirective,
    MapPipe,
    TruncateTooltipDirective,
    TreatmentCategoryIndicatorComponent,
    ChartedItemsSurfaceListComponent,
  ],
})
export class AppointmentHistoryCardTreatmentComponent {
  treatment$ = new ReplaySubject<WithRef<IChartedTreatment>>(1);
  config$: Observable<WithRef<ITreatmentConfiguration>>;
  treatmentCategory$: Observable<
    DocumentReference<ITreatmentCategory> | undefined
  >;
  showBasePrice$: Observable<boolean>;
  basePrice$: Observable<number>;
  serviceCodes$: Observable<IPricedServiceCodeEntry[]>;

  @Input()
  set treatment(treatment: WithRef<IChartedTreatment>) {
    if (treatment) {
      this.treatment$.next(treatment);
    }
  }

  constructor() {
    this.config$ = this.treatment$.pipe(
      switchMap((treatment) => getDoc(treatment.config.ref)),
      shareReplayCold()
    );
    this.treatmentCategory$ = this.config$.pipe(
      map(({ category }) => category)
    );
    this.serviceCodes$ = this.treatment$.pipe(
      map((treatment) => this._getServiceCodes(treatment))
    );
    this.showBasePrice$ = this.treatment$.pipe(
      map((treatment) => ChartedTreatment.getBasePrice(treatment) > 0)
    );
    this.basePrice$ = this.treatment$.pipe(
      map((treatment) => ChartedTreatment.getBasePrice(treatment))
    );
  }

  getCodeName(entry: IPricedServiceCodeEntry): string | undefined {
    return ServiceCodeEntry.getName(entry);
  }

  getCodeName$(entry: IPricedServiceCodeEntry): Observable<string | undefined> {
    return of(ServiceCodeEntry.getName(entry));
  }

  private _getServiceCodes(
    treatment: WithRef<IChartedTreatment>
  ): IPricedServiceCodeEntry[] {
    const smartGroupItems = treatment.serviceCodeSmartGroups.map((group) => {
      const selected = ChartedServiceSmartGroup.getSelected(group);
      return selected ? [selected] : group.serviceCodes;
    });
    const groupItems = treatment.serviceCodeGroups.map((group) => {
      if (group.type !== ServiceCodeGroupType.Exclusive) {
        return [];
      }
      const selected = ChartedServiceExclusiveGroup.getSelected(group);
      return selected ? [selected] : group.serviceCodes;
    });

    return [
      ...flatten(smartGroupItems),
      ...flatten(groupItems),
      ...treatment.serviceCodes,
    ].filter((serviceCode) => serviceCode.quantity > 0);
  }
}
